import { FormInstance } from "antd";
import { Error } from "app/based/baseApiModel/baseApiResponseModel/BaseApiResponseModel";
import { BaseFilterListRequestModel } from "app/based/baseApiModel/baseFilterListModel/BaseFilterListRequestModel";
import { BaseFilterListResponseModel } from "app/based/baseApiModel/baseFilterListModel/BaseFilterListResponseModel";
import { BaseViewModel } from "app/based/baseViewModel/BaseViewModel";
import { AirtimeManagementInjection } from "features/airTimeManagement/di/AirtimeManagementInjection";
import { AirtimeActionEntity } from "features/airTimeManagement/domain/entities/AirtimeActionEntity";
import { AirtimeDetailEntity } from "features/airTimeManagement/domain/entities/AirtimeDetailEntity";
import { AirtimeRequestEntity } from "features/airTimeManagement/domain/entities/AirtimeRequestEntity";
import { AirtimeResponseEntity } from "features/airTimeManagement/domain/entities/AirtimeResponseEntity";
import { BehaviorSubject } from "rxjs";
import { injectable } from "tsyringe";
import { getMessage } from "utils/helpersInTs/helpersInTs";
import { strings } from "utils/localizedStrings";

class AirTimeManangementInput implements Disposable {
  public fetchList = new BehaviorSubject<AirtimeRequestEntity>({});
  public fetchDetail = new BehaviorSubject<string>("");
  public fetchCarrier = new BehaviorSubject<BaseFilterListRequestModel>({});
  public fetchActions = new BehaviorSubject<{
    detailInfo?: AirtimeDetailEntity;
    body?: AirtimeActionEntity;
    action?: "create" | "update";
  }>({});
  public deleteAirTime = new BehaviorSubject<string>("");
  public loading = new BehaviorSubject<boolean>(false);

  [Symbol.dispose](): void {
    this.fetchList.complete();
    this.fetchDetail.complete();
    this.fetchCarrier.complete();
    this.deleteAirTime.complete();
    this.loading.complete();
  }
}

class AirTimeManagementOutput implements Disposable {
  public list = new BehaviorSubject<Array<AirtimeResponseEntity>>([]);
  public carrierList = new BehaviorSubject<Array<BaseFilterListResponseModel>>(
    [],
  );
  public savedDetail = new BehaviorSubject<AirtimeDetailEntity>(
    new AirtimeDetailEntity(),
  );
  public createUpdateSuccess = new BehaviorSubject<boolean>(false);
  public deleteSuccess = new BehaviorSubject<boolean>(false);
  public savedTotal = new BehaviorSubject<number>(0);
  public onError = new BehaviorSubject<Error>({});

  [Symbol.dispose](): void {
    this.list.complete();
    this.createUpdateSuccess.complete();
    this.deleteSuccess.complete();
    this.carrierList.complete();
    this.savedDetail.complete();
    this.savedTotal.complete();
  }
}

@injectable()
export class AirTimeManagementViewModel extends BaseViewModel<
  AirTimeManangementInput,
  AirTimeManagementOutput
> {
  constructor() {
    super(new AirTimeManangementInput(), new AirTimeManagementOutput());
  }

  private airtimeUseCase =
    AirtimeManagementInjection.getAirtimeManagementUseCase();

  public buildTableQuery(
    pagination: any,
    filterForm: React.RefObject<FormInstance<any>>,
    prevParams: AirtimeRequestEntity,
  ) {
    const formValues = filterForm?.current?.getFieldsValue(true) || {};
    const { created, ...rest } = formValues;

    const query = {
      ...prevParams,
      ...rest,
      page: pagination?.current - 1,
      limit: pagination?.pageSize,
    };

    const paginationState = {
      page: pagination?.current - 1,
      limit: pagination?.pageSize,
    };

    return {
      query,
      pagination: paginationState,
    };
  }

  protected override transform(): void {
    this.skipFirstInitial(this.input.fetchList, async (params) => {
      try {
        this.input.loading.next(true);
        const res = await this.airtimeUseCase?.getList(params);
        if (res?.data) {
          this.output.list.next(res?.data);
          this.output.savedTotal.next(res?.pagination?.total);
        }
      } catch (error) {
        console.error(error);
        this.output.list.next([]);
        this.output.savedTotal.next(0);
        this.output.onError.next(error);
      } finally {
        this.input.loading.next(false);
      }
    });

    this.skipFirstInitial(this.input.fetchCarrier, async (params) => {
      try {
        const res = await this.airtimeUseCase.getCarrier(params);
        if (res) {
          this.output.carrierList.next(
            AirtimeResponseEntity.mapCarrierWithId(res),
          );
        }
      } catch (error) {
        console.error(error);
      }
    });

    this.skipFirstInitial(this.input.fetchDetail, async (code) => {
      try {
        const res = await this.airtimeUseCase.getDetail({ code: code });
        if (res?.data) {
          this.output.savedDetail.next(res?.data);
        }
      } catch (error) {
        this.output.onError.next(error);
        console.error(error);
      }
    });

    this.skipFirstInitial(this.input.deleteAirTime, async (code) => {
      try {
        const res = await this.airtimeUseCase.deleteAirTime({
          code: code,
        });
        if (res?.data) {
          getMessage(strings.GlobalMessage.DeleteSuccessfully, 5, "success");
          this.output.deleteSuccess.next(true);
        }
      } catch (error) {
        console.error(error);
        this.output.onError.next(error);
      }
    });

    this.skipFirstInitial(this.input.fetchActions, async (props) => {
      const { action, body, detailInfo } = props;
      switch (action) {
        case "update":
          try {
            const res = await this.airtimeUseCase?.updateAirTime(
              body,
              detailInfo,
            );
            if (res?.data) {
              getMessage(
                strings.GlobalMessage.UpdateSuccessfully,
                4,
                "success",
              );
              this.output.createUpdateSuccess.next(true);
            }
          } catch (error) {
            console.error(error);
            this.output.onError.next(error);
          }
          break;
        case "create":
          try {
            const res = await this.airtimeUseCase?.createAirTime(
              body,
              detailInfo,
            );
            if (res?.data) {
              getMessage(
                strings.GlobalMessage.CreateSuccessfully,
                4,
                "success",
              );
              this.output.createUpdateSuccess.next(true);
            }
          } catch (error) {
            console.error(error);
            this.output.onError.next(error);
          }
          break;
      }
    });
  }
}
